import React, { useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import TextureOverlay from '../components/team/TextureOverlay';
import SiteHeader from '../components/team/SiteHeader';
import CoffeeSpecsAndPricing from '../components/team/CoffeeSpecsAndPricing';
import LogisticsDelivery from '../components/team/LogisticsDelivery';
import Footer from '../components/team/Footer';
import SectionDivider from '../components/team/SectionDivider';

const b2bFaqs = [
  {
    question: 'What is the minimum order quantity for wholesale?',
    answer: 'There is no fixed MOQ. We supply small roasters with 25 to 50 kg trial orders and scale up to full container loads for café chains and instant coffee manufacturers. Volume pricing kicks in at 500 kg, with steeper discounts at 1 ton and full-container quantities.'
  },
  {
    question: 'Can I request a sample before placing a bulk order?',
    answer: 'Yes. We send 250 g samples of any screen size (SC-12, SC-15, SC-18) to roasters and cafés in Pakistan. Sample cost is refunded against your first paid order. Message us on WhatsApp with your roastery address and which grade you want to evaluate.'
  },
  {
    question: 'What is the lead time for wholesale orders within Pakistan?',
    answer: 'Orders dispatch from our Karachi warehouse and are delivered in 2-3 days nationwide. All stock is duty-paid and warehoused locally, so there is no waiting on customs or shipping from Uganda.'
  },
  {
    question: 'Do you supply roasters and cafés outside Karachi?',
    answer: 'Yes. We dispatch nationwide to your roastery or café, delivered in 2-3 days, with active wholesale relationships in Karachi, Lahore, Islamabad, and Faisalabad. Delivery within Karachi is included in the price. Freight to other cities and international shipments is charged separately.'
  },
  {
    question: 'What payment terms do you offer wholesale buyers?',
    answer: 'First order is bank transfer or cash on delivery. Repeat buyers on regular schedules can move to net-15 or net-30 terms after a relationship is established. We do not work with unsecured credit on first orders.'
  },
  {
    question: 'Do you provide private label or custom roasting?',
    answer: 'We roast to your spec on request, for café chains and private-label coffee brands. Send us your target roast profile (city, full city, vienna, etc.) and order volume and we will quote per kg roasted. Whole bean or pre-ground packaging available.'
  },
  {
    question: 'Are the green beans Halal and traceable?',
    answer: 'Yes. Single origin from Mubarak Farm in Nabutaka, Luweero District, Uganda. No additives, no processing agents, suitable for Halal consumption. Each lot ships with a spec sheet listing harvest date, moisture, screen size, and cupping notes.'
  },
  {
    question: 'Are these the green coffee beans sold for weight loss?',
    answer: 'No. These are raw, unroasted Robusta beans sold to roasters, who roast them for brewing. They are not green coffee extract or a supplement, and we make no health claims about them.'
  },
  {
    question: 'What does the wholesale PKR price include?',
    answer: 'Duty paid and stocked in Karachi. The price covers customs clearance, import duties, warehouse handling, and delivery within Karachi. Freight to other cities in Pakistan and international shipping is charged separately, based on order volume and destination.'
  }
];

const meta = {
  title: 'Uganda Robusta Green Beans in Pakistan | Mubarak Farm',
  meta: [
    { name: 'description', content: 'Farm-direct Uganda Robusta green beans in Karachi. SC-12, SC-15, SC-18 from PKR 2,700/kg, duty-paid, delivered in 2-3 days. Samples on request.' },
    { name: 'keywords', content: 'wholesale coffee supplier pakistan, green coffee beans karachi, robusta wholesale pakistan, cafe supplier pakistan, coffee distributor karachi, roastery supplier pakistan, bulk green coffee pakistan, sc-12 sc-15 sc-18 wholesale, ugandan robusta supplier, halal wholesale coffee, single origin wholesale pakistan, coffee for cafes lahore, coffee for cafes islamabad, instant coffee manufacturer supplier, private label coffee pakistan' },
    { name: 'author', content: 'Mubarak Farm Uganda' },
    { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    { property: 'og:title', content: 'Uganda Robusta Green Beans in Pakistan | Mubarak Farm' },
    { property: 'og:description', content: 'Farm-direct Uganda Robusta green beans in Karachi. SC-12, SC-15, SC-18 from PKR 2,700/kg, duty-paid, delivered in 2-3 days. Samples on request.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://mubarakfarmug.com/wholesale' },
    { property: 'og:image', content: 'https://mubarakfarmug.com/flow-assets/content/og-mubarak-farm.jpg' },
    { property: 'og:locale', content: 'en_PK' },
    { property: 'og:site_name', content: 'Mubarak Farm Uganda' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/jpeg' },
    { property: 'og:image:alt', content: 'Ripening coffee cherries on a branch' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Uganda Robusta Green Beans in Pakistan | Mubarak Farm' },
    { name: 'twitter:description', content: 'Farm-direct Uganda Robusta green beans in Karachi. SC-12, SC-15, SC-18 from PKR 2,700/kg, duty-paid, delivered in 2-3 days. Samples on request.' },
    { name: 'twitter:image', content: 'https://mubarakfarmug.com/flow-assets/content/og-mubarak-farm.jpg' },
    { name: 'geo.region', content: 'PK-SD' },
    { name: 'geo.placename', content: 'Karachi' },
    { name: 'geo.position', content: '24.8607;67.0011' },
    { name: 'ICBM', content: '24.8607, 67.0011' }
  ],
  link: [
    { rel: 'icon', type: 'image/png', href: '/favicon.png' },
    { rel: 'canonical', href: 'https://mubarakfarmug.com/wholesale' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': 'https://mubarakfarmug.com/wholesale#webpage',
        'url': 'https://mubarakfarmug.com/wholesale',
        'name': 'Uganda Robusta Green Beans in Pakistan',
        'description': 'Wholesale Uganda Robusta green beans from Mubarak Farm for roasters, café chains, and instant coffee manufacturers across Pakistan.',
        'inLanguage': 'en-PK',
        'isPartOf': { '@type': 'WebSite', '@id': 'https://mubarakfarmug.com/#website', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'about': { '@id': 'https://mubarakfarmug.com/wholesale#wholesale-service' },
        'mainEntity': { '@id': 'https://mubarakfarmug.com/wholesale#green-beans' },
        'primaryImageOfPage': {
          '@type': 'ImageObject',
          'url': 'https://mubarakfarmug.com/flow-assets/content/coffee-cherries-drying.jpg'
        }
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://mubarakfarmug.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Wholesale', 'item': 'https://mubarakfarmug.com/wholesale' }
        ]
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': 'https://mubarakfarmug.com/wholesale#green-beans',
        'name': 'Uganda Robusta Green Coffee Beans (SC-12, SC-15, SC-18)',
        'brand': { '@type': 'Brand', 'name': 'Mubarak Farm' },
        'manufacturer': { '@type': 'Organization', '@id': 'https://mubarakfarmug.com/#organization', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'description': 'Single-origin Uganda Robusta green coffee beans from Mubarak Farm, Luweero District. Natural process, sun dried on raised beds, 11.8% moisture. Held duty-paid in Karachi for wholesale supply to roasters, café chains, and instant coffee manufacturers in Pakistan.',
        'category': 'Coffee & Tea > Coffee Beans > Green Coffee Beans',
        'image': 'https://mubarakfarmug.com/flow-assets/content/coffee-cherries-drying.jpg',
        'sku': 'MUBARAK-ROBUSTA-GREEN-SC121518',
        'productID': 'mubarak-farm-robusta-green-beans',
        'additionalProperty': [
          { '@type': 'PropertyValue', 'name': 'Coffee Type', 'value': 'Robusta' },
          { '@type': 'PropertyValue', 'name': 'Origin', 'value': 'Nabutaka, Luweero District, Uganda' },
          { '@type': 'PropertyValue', 'name': 'Altitude', 'value': '1100-1200 MASL' },
          { '@type': 'PropertyValue', 'name': 'Harvest Date', 'value': 'February 2026' },
          { '@type': 'PropertyValue', 'name': 'Process', 'value': 'Natural, sun dried on raised beds' },
          { '@type': 'PropertyValue', 'name': 'Moisture Level', 'value': '11.8%' },
          { '@type': 'PropertyValue', 'name': 'Screen Sizes', 'value': 'SC-12, SC-15, SC-18' }
        ],
        'offers': {
          '@type': 'AggregateOffer',
          'availability': 'https://schema.org/InStock',
          'priceCurrency': 'PKR',
          'url': 'https://mubarakfarmug.com/wholesale',
          'offerCount': '3',
          'lowPrice': '2700',
          'highPrice': '3300',
          'seller': { '@id': 'https://mubarakfarmug.com/wholesale#pakistan-office' },
          'offers': [
            { '@type': 'Offer', 'name': 'SC-18 Green Beans, per kg', 'price': '3300', 'priceCurrency': 'PKR', 'availability': 'https://schema.org/InStock', 'url': 'https://mubarakfarmug.com/wholesale#specs-pricing', 'priceValidUntil': '2026-12-31' },
            { '@type': 'Offer', 'name': 'SC-15 Green Beans, per kg', 'price': '3000', 'priceCurrency': 'PKR', 'availability': 'https://schema.org/InStock', 'url': 'https://mubarakfarmug.com/wholesale#specs-pricing', 'priceValidUntil': '2026-12-31' },
            { '@type': 'Offer', 'name': 'SC-12 Green Beans, per kg', 'price': '2700', 'priceCurrency': 'PKR', 'availability': 'https://schema.org/InStock', 'url': 'https://mubarakfarmug.com/wholesale#specs-pricing', 'priceValidUntil': '2026-12-31' }
          ]
        }
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WholesaleStore',
        '@id': 'https://mubarakfarmug.com/wholesale#pakistan-office',
        'name': 'Mubarak Farm Pakistan, Wholesale Uganda Robusta',
        'description': 'Wholesale supplier of Uganda Robusta green beans in Pakistan. Duty-paid stock held in Karachi and delivered in 2-3 days nationwide. Spec sheet, PKR pricing, and samples available.',
        'url': 'https://mubarakfarmug.com/wholesale',
        'image': 'https://mubarakfarmug.com/flow-assets/content/coffee-cherries-drying.jpg',
        'telephone': '+44 771 608 9293',
        'email': 'mubarakfarm53@gmail.com',
        'parentOrganization': { '@type': 'Organization', '@id': 'https://mubarakfarmug.com/#organization', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'address': { '@type': 'PostalAddress', 'addressLocality': 'Karachi', 'addressRegion': 'Sindh', 'addressCountry': 'PK' },
        'geo': { '@type': 'GeoCoordinates', 'latitude': 24.8607, 'longitude': 67.0011 },
        'openingHoursSpecification': [
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            'opens': '09:00',
            'closes': '18:00'
          }
        ],
        'currenciesAccepted': 'PKR',
        'paymentAccepted': 'Cash, Bank Transfer',
        'areaServed': { '@type': 'Country', 'name': 'Pakistan' }
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': 'https://mubarakfarmug.com/wholesale#wholesale-service',
        'serviceType': 'Wholesale Green Coffee Supply',
        'name': 'Wholesale Uganda Robusta Supply for Roasters and Cafés in Pakistan',
        'description': 'Wholesale supply of single-origin Uganda Robusta to roasters, coffee shops, and café chains across Pakistan. SC-12, SC-15, and SC-18 green beans held duty-paid in Karachi. PKR pricing per kg, volume tiers from 500 kg, samples on request.',
        'provider': { '@type': 'Organization', '@id': 'https://mubarakfarmug.com/#organization', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'areaServed': { '@type': 'Country', 'name': 'Pakistan' },
        'audience': {
          '@type': 'BusinessAudience',
          'audienceType': 'Coffee roasters, coffee shops, café chains, hotels, restaurants, instant coffee manufacturers'
        },
        'url': 'https://mubarakfarmug.com/wholesale'
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': 'https://mubarakfarmug.com/wholesale#faq',
        'mainEntity': b2bFaqs.map((f) => ({
          '@type': 'Question',
          'name': f.question,
          'acceptedAnswer': { '@type': 'Answer', 'text': f.answer }
        }))
      })
    }
  ]
};

function B2BHero() {
  return (
    <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">
              <li><a href="/" className="hover:text-clay transition-colors">Home</a></li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-gray-900 font-semibold">Wholesale</li>
            </ol>
          </nav>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Wholesale supply, Pakistan</p>

          <h1 className="font-heading text-4xl xs:text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-gray-900 mb-6 max-w-4xl">
            <span className="text-clay">Uganda Robusta</span> green beans, duty-paid in Karachi, delivered in <span className="whitespace-nowrap">2-3 days</span>.
          </h1>

          <p className="text-lg text-gray-700 max-w-2xl leading-relaxed mb-10">
            Wholesale supply of SC-12, SC-15, and SC-18 green Ugandan Robusta to roasters, café chains, and instant coffee manufacturers across Pakistan. Transparent PKR pricing, volume tiers on 500 kg and up, samples on request.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="https://wa.me/447716089293?text=Hello%20Mubarak%20Farm%2C%20I%27d%20like%20to%20request%20a%20wholesale%20sample."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-8 py-4 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
            >
              Request a sample
            </a>
            <a
              href="#specs-pricing"
              className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-clay text-white font-bold px-8 py-4 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
            >
              See spec sheet and pricing
            </a>
          </div>

          <p className="mt-8 text-sm text-gray-600 max-w-2xl">
            25+ tons sold to date. Active wholesale buyers in Karachi, Lahore, Islamabad, and Faisalabad. PKR per kg pricing with Karachi delivery included. Other cities and international freight charged separately.
          </p>

          <figure className="mt-12 lg:mt-16">
            <img
              className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] object-cover rounded-2xl"
              src="/flow-assets/content/farm/green-beans-sample-1200.webp"
              srcSet="/flow-assets/content/farm/green-beans-sample-1200.webp 1200w, /flow-assets/content/farm/green-beans-sample-2000.webp 2000w"
              sizes="(min-width: 1216px) 1152px, 100vw"
              width="2000"
              height="1125"
              alt="A hand holding a heap of hulled green Robusta beans over a plate of more beans"
            />
            <figcaption className="mt-3 text-sm text-gray-600">Green Robusta from Mubarak Farm.</figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

function VolumeTiers() {
  const tiers = [
    { volume: '25 to 100 kg', label: 'Trial', priceNote: 'List PKR per kg', leadTime: '2-3 days, Karachi delivery included', payment: 'Bank transfer or cash on delivery', use: 'Small roasters evaluating the harvest' },
    { volume: '100 to 500 kg', label: 'Standing order', priceNote: 'Up to 5% off list', leadTime: '2-3 days nationwide', payment: 'Bank transfer, net-15 after first order', use: 'Cafés moving 50 to 100 kg a month' },
    { volume: '500 kg to 1 ton', label: 'Volume', priceNote: 'Quoted per lot, typically 8 to 12% off list', leadTime: '2-3 days', payment: 'Net-15 to net-30 on standing terms', use: 'Café chains, micro-roasters scaling' },
    { volume: '1 ton+ / FCL', label: 'Container', priceNote: 'FCL quote, contact for terms', leadTime: 'Scheduled, 1 to 2 weeks', payment: 'Net-30 on standing terms', use: 'Instant coffee manufacturers, large roasters' }
  ];
  const columns = [
    { label: 'Order size and tier', span: 'col-span-3' },
    { label: 'Price', span: 'col-span-2' },
    { label: 'Lead time', span: 'col-span-2' },
    { label: 'Payment', span: 'col-span-2' },
    { label: 'Best for', span: 'col-span-3' }
  ];
  return (
    <section id="volume-tiers" className="relative py-20 lg:py-28">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Volume tiers and lead times</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Price, lead time and payment terms by order size
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              Wholesale pricing in PKR per kg is the list. Volume discounts and credit terms unlock at the tiers below. Delivery within Karachi is included in the price. Freight to other cities is charged separately.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden">
            <div className="hidden lg:grid grid-cols-12 gap-6 px-8 py-4 border-b border-gray-200">
              {columns.map((c) => (
                <div key={c.label} className={`${c.span} text-xs font-semibold uppercase tracking-wider text-gray-500`}>{c.label}</div>
              ))}
            </div>
            <ul className="divide-y divide-gray-200">
              {tiers.map((t) => (
                <li key={t.volume} className="grid grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-4 px-6 lg:px-8 py-6">
                  <div className="col-span-2 lg:col-span-3">
                    <p className="font-heading text-2xl text-gray-900 lining-nums tabular-nums whitespace-nowrap">{t.volume}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-clay">{t.label}</p>
                  </div>
                  {[
                    ['Price', t.priceNote, 'lg:col-span-2'],
                    ['Lead time', t.leadTime, 'lg:col-span-2'],
                    ['Payment', t.payment, 'lg:col-span-2'],
                    ['Best for', t.use, 'lg:col-span-3']
                  ].map(([label, value, span]) => (
                    <div key={label} className={span}>
                      <p className="lg:hidden text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
                      <p className={`text-sm leading-relaxed ${label === 'Best for' ? 'text-gray-600' : 'text-gray-800'}`}>{value}</p>
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-sm text-gray-600 mt-6 max-w-2xl leading-relaxed">
            Discounts shown are typical. Final quote depends on screen mix (SC-12 / SC-15 / SC-18), packaging, and freight for deliveries outside Karachi. Quoted volumes are per shipment, not annual contract minimums.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhoWeSupply() {
  const audiences = [
    {
      label: 'Specialty roasters',
      copy: 'Boutique roasters in Karachi, Lahore, and Islamabad sourcing single origin Robusta for espresso blends, dark roasts, and house drip programs.',
      details: '25 to 200 kg orders, monthly cadence'
    },
    {
      label: 'Café chains',
      copy: 'Multi-location cafés ordering standing volumes for their roastery or for white-label roasting partners. Roasted and ground options available.',
      details: '100 to 500 kg, weekly or fortnightly'
    },
    {
      label: 'Instant coffee manufacturers',
      copy: 'Pakistani instant coffee producers using SC-12 and SC-15 for high-volume base blends. Container-load quantities, scheduled delivery.',
      details: 'Full container, scheduled monthly'
    },
    {
      label: 'Hotels and restaurants',
      copy: 'Hospitality groups needing consistent house coffee. We supply green to your roasting partner or ship pre-roasted whole bean and ground.',
      details: '50 to 200 kg, standing orders'
    }
  ];

  return (
    <section id="who-we-supply" className="relative py-20 lg:py-28">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Who we supply</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Four kinds of wholesale buyer
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              If you don't fit cleanly into one of these, message us anyway. We will tell you straight whether we are the right supplier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {audiences.map((a) => (
              <div key={a.label} className="bg-white rounded-3xl border border-gray-200 p-6 lg:p-8 flex flex-col">
                <h3 className="font-heading text-2xl lg:text-3xl text-gray-900 mb-3">{a.label}</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-6">{a.copy}</p>
                <p className="mt-auto md:min-h-[3rem] pt-4 border-t border-gray-200 text-xs font-semibold uppercase tracking-[0.2em] text-clay">{a.details}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function B2BContact() {
  return (
    <section id="b2b-contact" className="relative py-20 lg:py-28">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Wholesale desk</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Talk to the Pakistan wholesale team
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              One contact, one WhatsApp thread, one PKR quote. Tell us your roastery, your monthly volume, and which screen sizes you want to evaluate. We respond the same business day.
            </p>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-8 sm:gap-x-12 mb-12 border-t border-gray-300/70 pt-10">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-2">WhatsApp, Pakistan desk</dt>
              <dd>
                <a href="https://wa.me/447716089293" target="_blank" rel="noopener noreferrer" className="font-heading text-xl text-gray-900 hover:text-clay transition-colors">+44 771 608 9293</a>
                <p className="text-sm text-gray-600 mt-2">Mon to Sat, 09:00 to 18:00 PKT.</p>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-2">Email</dt>
              <dd>
                <a href="mailto:mubarakfarm53@gmail.com" className="font-heading text-xl text-gray-900 hover:text-clay transition-colors [overflow-wrap:anywhere]">mubarakfarm53@gmail.com</a>
                <p className="text-sm text-gray-600 mt-2">Send your roastery details and monthly volume.</p>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-2">Stock</dt>
              <dd>
                <p className="font-heading text-xl text-gray-900 leading-tight">Karachi warehouse</p>
                <p className="text-sm text-gray-600 mt-2">Duty-paid, ex-stock. Karachi delivery included.</p>
              </dd>
            </div>
          </dl>

          <a
            href="https://wa.me/447716089293?text=Hello%20Mubarak%20Farm%20wholesale%20desk%2C%20I%27d%20like%20to%20discuss%20a%20bulk%20order."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto text-center bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-6 sm:px-10 py-4 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
          >
            Start a wholesale conversation
          </a>
        </div>
      </div>
    </section>
  );
}

function B2BFaqSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="b2b-faq" className="py-20 lg:py-28 relative">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* Header and WhatsApp prompt, sticky beside the list on desktop.
              top-36 clears the fixed stock bar plus the wholesale nav. */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-36">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Wholesale FAQ</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-6">
                Questions roasters and cafés ask
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Ready to evaluate the harvest? Send your roastery details and the grade you want to sample.
              </p>
              <a
                href="https://wa.me/447716089293?text=Hello%20Mubarak%20Farm%2C%20I%27d%20like%20to%20request%20a%20wholesale%20sample."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-gold-500 hover:bg-gold-400 text-gray-900 px-6 xl:px-8 py-4 rounded-full font-bold transition-[background-color,transform] duration-200 active:scale-[0.97]"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span>WhatsApp wholesale desk</span>
              </a>
              <p className="mt-3 text-sm text-gray-600">Sample cost refunded against your first order.</p>
            </div>
          </div>

          <ul className="lg:col-span-8 divide-y divide-gray-300/70 border-y border-gray-300/70">
            {b2bFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <li key={faq.question}>
                  <button
                    onClick={() => toggle(index)}
                    className="w-full py-6 flex items-center justify-between gap-6 text-left rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-4 focus-visible:ring-offset-oat"
                    aria-expanded={isOpen}
                  >
                    <span className="font-heading text-xl lg:text-2xl text-gray-900">{faq.question}</span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                      className={`flex-shrink-0 text-clay transition-transform duration-300 motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                    >
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="pb-6 pr-10 animate-fade-in motion-reduce:animate-none">
                      <div className="text-gray-700 leading-relaxed whitespace-pre-line max-w-2xl">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

        </div>
      </div>
    </section>
  );
}

export default function Wholesale() {
  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet {...meta}></Helmet>
      </HelmetProvider>
      <TextureOverlay fixed />
      <SiteHeader current="/wholesale" />
      <main>
        <B2BHero />
        <CoffeeSpecsAndPricing />
        <SectionDivider />
        <VolumeTiers />
        <SectionDivider />
        <WhoWeSupply />
        <SectionDivider />
        <LogisticsDelivery />
        <SectionDivider />
        <B2BFaqSection />
        <SectionDivider />
        <B2BContact />
      </main>
      <Footer />
    </React.Fragment>
  );
}
